import React, { ReactNode, cloneElement } from 'react';
import { classNames } from '../../utils/style';
import { Image } from '../../components/Image';
import { useScreenSize } from '../../utils/utils';
import {
  textBody,
  textBodyUppercaseSmall,
  textCaption,
  textTitle1,
} from '../../styles/typography';

import {
  animationImage,
  applyCardContentStyle,
  applyCardStyle,
  applyCtaButtonStyle,
  applyLogoContentStyle,
  applyQuoteWithImgStyle,
  applyTextStyle,
  transitionAnimation,
} from './logic';
import { CardProps } from '.';
import { TitleWithArrow } from '../ButtonV2/TitleWithArrow';
interface CtaButtonProps {
  title?: ReactNode;
  className?: string;
}

export const QuoteCard = ({
  children,
  ctaButton,
  companyDescription,
  text,
  name,
  jobPosition,
  logoPosition,
  className,
  logo,
  image,
  title,
  cardsBackgroundColor,
  disableBorder,
  type,
  imagePosition,
  capsText,
  as: Component = 'div',
}: CardProps) => {
  const screenSize = useScreenSize();
  const ctaProps = ctaButton && (ctaButton.props as CtaButtonProps);
  const newCta =
    ctaButton &&
    cloneElement(ctaButton, {
      className: applyCtaButtonStyle(type),
      title:
        ctaButton.props && ctaProps ? (
          <TitleWithArrow title={ctaProps.title} />
        ) : undefined,
    } as CtaButtonProps);

  return (
    <Component
      className={classNames(
        'QuoteCard',
        'max-w-[90vw] md:max-w-[70vw] lg:max-w-none',
        'rounded-18 flex gap-24',
        !disableBorder && 'border-2 border-neutral-51',
        type === 'quoteWithImg'
          ? applyQuoteWithImgStyle(imagePosition, cardsBackgroundColor || false)
          : applyCardStyle(type),
        className,
      )}
    >
      <div className={applyCardContentStyle(type)}>
        {capsText && (
          <span
            className={classNames(
              textBodyUppercaseSmall,
              'text-neutral-101 mb-24',
            )}
          >
            {capsText}
          </span>
        )}
        {logo && (logoPosition === 'content' || type !== 'quoteWithImg') && (
          <div className={`${applyLogoContentStyle(type)}`}>
            <Image {...logo} className="w-auto h-full" sizes={['120px']} />
          </div>
        )}
        {title && type === 'quoteWithImg' && (
          <p className={classNames(textTitle1, 'mb-24 text-neutral-301')}>
            {title}
          </p>
        )}
        {text && (
          <figure>
            <blockquote
              className={applyTextStyle(type)}
              style={
                type === 'quoteWithImg'
                  ? { quotes: `'"' '"'` }
                  : { quotes: "'« ' ' »'" }
              }
            >
              <q>{text}</q>
            </blockquote>

            {type === 'quoteWithImg' && (
              <figcaption>
                {name && (
                  <div
                    className={classNames(
                      textBody,
                      'text-neutral-301 hidden md:block',
                    )}
                  >
                    {name}
                  </div>
                )}
                {jobPosition && (
                  <div
                    className={classNames(
                      textCaption,
                      'text-neutral-101 hidden md:block',
                    )}
                  >
                    {jobPosition}
                  </div>
                )}
              </figcaption>
            )}
          </figure>
        )}
        {(type === 'quote' || type === 'multipleQuotes') && (
          <div className={'flex gap-16 mt-24'}>
            <div
              className={classNames(
                'hidden sm:block overflow-hidden relative rounded-6',
              )}
              style={{
                height: '60px',
                width: '60px',
              }}
            >
              {image && (
                <Image
                  {...image}
                  className={classNames(
                    'w-full h-full object-cover',
                    transitionAnimation,
                    animationImage,
                  )}
                  sizes={['307px']}
                />
              )}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(rgba(0, 0, 0, 0.13), rgba(0, 0, 0, 0.13))',
                }}
              />
            </div>
            <div className={classNames('flex flex-col justify-center')}>
              {name && (
                <div className={classNames(textBody, 'text-neutral-301')}>
                  {name}
                </div>
              )}
              {jobPosition && (
                <div className={classNames(textCaption, 'text-neutral-101')}>
                  {jobPosition}
                </div>
              )}
            </div>
          </div>
        )}
        {companyDescription && (
          <div
            className={classNames(
              textCaption,
              'text-neutral-101 hidden md:block',
            )}
          >
            {companyDescription}
          </div>
        )}
        {children}
        {newCta && newCta}
      </div>
      {type === 'quoteWithImg' && (
        <div
          className={classNames('relative overflow-hidden rounded-12 lg:w-1/2')}
          style={{
            height: screenSize === 'xs' ? '250px' : 'auto',
          }}
        >
          {image && (
            <Image
              {...image}
              className={classNames(
                'w-full h-full object-cover',
                transitionAnimation,
                animationImage,
              )}
              sizes={['307px']}
            />
          )}
          <div
            className={classNames('absolute inset-0')}
            style={{
              background:
                'linear-gradient(rgba(0, 0, 0, 0.13), rgba(0, 0, 0, 0.13))',
            }}
          />
          {logo && logoPosition === 'image' && (
            <div
              className={classNames(
                'hidden md:flex absolute',
                'rounded-6 bg-neutral-1 bottom-24',
                'left-24 p-16 h-64 w-max',
              )}
            >
              <Image {...logo} className="w-auto h-full" sizes={['307px']} />
            </div>
          )}
          {name && jobPosition && (
            <div
              className={classNames(
                'absolute rounded-6 bg-neutral-1',
                'left-12 right-12 bottom-12',
                'p-8 block md:hidden',
              )}
            >
              <div className={classNames(textBody, 'text-neutral-301')}>
                {name}
              </div>
              <div className={classNames(textCaption, 'text-neutral-101')}>
                {jobPosition}
              </div>
            </div>
          )}
        </div>
      )}
    </Component>
  );
};
export default QuoteCard;
